import type { CSSProperties } from 'react'
import type { HrLeaveCounts, HrLeavesFlag } from '../../api/hr-leaves'
import { Icon, type IconName } from '../primitives/Icon'

/** A bg / fg / line accent triple on the app's semantic tokens, so the cards
 *  stay theme-aware (light + dark). Same vocabulary as TeamStatCards. */
interface Accent {
  bg: string
  fg: string
  line: string
}

const accent = (tone: 'warning' | 'danger' | 'info'): Accent => ({
  bg: `var(--${tone}-bg)`,
  fg: `var(--${tone}-fg)`,
  line: `var(--${tone}-line)`,
})

/** Grey — matches the gray `pending hr review` status chip in the table. */
const NEUTRAL: Accent = { bg: 'var(--surface-2)', fg: 'var(--text-2)', line: 'var(--line)' }

interface CardDef {
  flag: HrLeavesFlag
  countKey: keyof HrLeaveCounts
  label: string
  icon: IconName
  accent: Accent
}

const CARDS: ReadonlyArray<CardDef> = [
  { flag: 'above_capacity', countKey: 'aboveCapacity', label: 'Above capacity', icon: 'groups', accent: accent('warning') },
  { flag: 'emergency', countKey: 'emergency', label: 'Emergency', icon: 'emergency', accent: accent('danger') },
  { flag: 'out_of_system', countKey: 'outOfSystem', label: 'Out of system', icon: 'edit_note', accent: accent('info') },
  { flag: 'pending_hr_review', countKey: 'pendingHrReview', label: 'Pending HR review', icon: 'fact_check', accent: NEUTRAL },
]

function cardStyle(a: Accent, active: boolean): CSSProperties {
  return {
    flex: '1 1 180px',
    minWidth: 0,
    minHeight: 44,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '14px 16px',
    borderRadius: 12,
    background: a.bg,
    textAlign: 'left',
    boxSizing: 'border-box',
    cursor: 'pointer',
    // Active reads as "selected": a ring + heavier border, not a colour swap.
    border: `1px solid ${active ? a.fg : a.line}`,
    boxShadow: active ? `0 0 0 1px ${a.fg} inset` : 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
  }
}

/**
 * Clickable filter cards above the HR Applied Leaves table. Each card shows
 * how many leaves fall in its category under the list's other filters, and
 * toggles the list to just that category (one card at a time; click the
 * active card again to clear).
 */
export function HrLeaveFilterCards({
  counts,
  active,
  onToggle,
}: Readonly<{
  /** Undefined while loading — cards render a placeholder count. */
  counts: HrLeaveCounts | undefined
  active: HrLeavesFlag | null
  onToggle: (flag: HrLeavesFlag) => void
}>): JSX.Element {
  return (
    <div
      data-testid="hr-leave-filter-cards"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}
    >
      {CARDS.map((card) => {
        const isActive = active === card.flag
        return (
          <button
            key={card.flag}
            type="button"
            aria-pressed={isActive}
            onClick={() => onToggle(card.flag)}
            title={isActive ? `Showing only ${card.label.toLowerCase()} leaves · click to clear` : `Show only ${card.label.toLowerCase()} leaves`}
            style={cardStyle(card.accent, isActive)}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name={card.icon} size={15} color={card.accent.fg} />
              <span style={{ fontSize: 12, fontWeight: 600, color: card.accent.fg }}>{card.label}</span>
            </span>
            <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
              {counts ? counts[card.countKey] : '—'}
            </span>
            <span style={{ fontSize: 11, color: card.accent.fg, opacity: 0.85 }}>
              {isActive ? 'Filtering the list · click to clear' : 'Click to filter the list'}
            </span>
          </button>
        )
      })}
    </div>
  )
}
